import React, { ReactElement, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { List } from 'immutable';

// Components
import TagsSearchBar from './components/TagsSearchBar';
import TagsCreateButtons from './components/TagsCreateButtons';
import TagLists from './components/TagLists';

// Utils
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';
import useTagHandlers from './hooks/useTagHandlers';

// Selectors
import { getElement } from '../../../../../element/selectors/elementSelector';
import { getSelectedTaskIdSelector } from '../../../selector/selectionToolsSelector';

// Types
import { TagsToolContentProps } from './tagComponentPropTypes';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

import './TagsToolContent.scss';
import { generateTagsByBoardsArray } from './tagUtils';

const TagsToolContent = (props: TagsToolContentProps): ReactElement => {
    const { handleClickEdit, selectedElements, allTags, userRootPath } = props;

    const [searchString, setSearchString] = useState<string>('');

    const elementIds = selectedElements.map(getElementId).toArray();
    const selectedTaskListId = useSelector(getSelectedTaskIdSelector);
    const focusElementIds = selectedTaskListId ? [selectedTaskListId] : elementIds;
    const elements: List<ImMNElement> = useSelector((state) => {
        return List(focusElementIds.map((elementId) => getElement(state, { elementId })));
    });

    const tagsByBoards = useMemo(
        () => generateTagsByBoardsArray(allTags, userRootPath, searchString),
        [allTags, userRootPath, searchString],
    );

    const { handleToggleTagSelection, handleCreateTag, handleCreateCopyAndAddToElement, handleSearchSubmit } =
        useTagHandlers(props, searchString, setSearchString, elements, tagsByBoards);

    const showCreateButtons = useMemo(
        () => searchString && !allTags.some((tag) => tag.name === searchString),
        [searchString, allTags],
    );
    return (
        <div className="TagsToolContent">
            <TagsSearchBar value={searchString} onChange={setSearchString} handleSearchSubmit={handleSearchSubmit} />
            <TagLists
                handleToggleTagSelection={handleToggleTagSelection}
                handleClickEdit={handleClickEdit}
                handleCreateCopyAndAddToElement={handleCreateCopyAndAddToElement}
                elements={elements}
                tagsByBoards={tagsByBoards}
            />
            {showCreateButtons && <TagsCreateButtons handleCreateTag={handleCreateTag} userRootPath={userRootPath} />}
        </div>
    );
};

export default TagsToolContent;
